import { ROLES, type Role } from '@volee/api-client'
import {
  IconBench,
  IconCalendar,
  IconCard,
  IconClub,
  type IconComponent,
  IconFamily,
  IconGoalPost,
  IconPlayer,
  IconRequests,
  IconStaff,
  IconTeam,
  IconTrophy,
  IconUser,
} from '@volee/ui'

import { CALENDAR_READERS, TEAMS_READERS } from '../auth/roles.ts'
import { messages } from '../messages/index.ts'
import { NAV_PATHS } from './paths.ts'

// Four accesses, four menus — from three rows to eleven, all flat: no heading groups them, and
// an entry's `roles` must match its route's. apps/frontend/docs/menu-quattro-accessi.md.

export interface NavigationEntry {
  /** The destination, and the section it owns: a nested route marks its parent. */
  readonly path: string
  readonly label: string
  readonly icon: IconComponent
  readonly active: boolean
}

interface EntrySpec {
  readonly path: string
  readonly label: string
  readonly icon: IconComponent
  /** Narrower than the group's, when one entry belongs to fewer facets than the others. */
  readonly roles?: readonly Role[]
}

interface GroupSpec {
  readonly roles: readonly Role[]
  readonly entries: readonly EntrySpec[]
}

const ui = messages.official
const menu = messages.navigation.menu

// Everyone who holds something: `ROLES` contains `guest`, which is the absence of a facet.
// What that cost: apps/frontend/docs/menu-quattro-accessi.md.
export const ANY_FACET: readonly Role[] = ROLES.filter((role) => role !== 'guest')

// `/players` is deliberately absent: it is §4.4's demo with four invented names. And there is
// no «Home» row, because `/` is the login door and bounces every session to its own profile.
const GROUPS: readonly GroupSpec[] = [
  {
    roles: ANY_FACET,
    entries: [
      {
        path: NAV_PATHS.officialDashboard,
        label: ui.dashboard.title,
        icon: IconClub,
        roles: ['club'],
      },
      {
        path: NAV_PATHS.myCoach,
        label: messages.myCoach.title,
        icon: IconBench,
        roles: ['coach'],
      },
      {
        path: NAV_PATHS.myParent,
        label: messages.myParent.title,
        icon: IconFamily,
        roles: ['parent'],
      },
      {
        path: NAV_PATHS.myPlayer,
        label: messages.myPlayer.title,
        icon: IconUser,
        roles: ['player'],
      },
      // Spec §3 rule 1: officials and coaches neither pay nor see this row.
      {
        path: NAV_PATHS.subscriptions,
        label: menu.subscriptions,
        icon: IconCard,
        roles: ['player', 'parent'],
      },
    ],
  },
  {
    roles: ['club'],
    entries: [
      { path: NAV_PATHS.officialRequests, label: menu.requests, icon: IconRequests },
      { path: NAV_PATHS.officialPeople, label: menu.staff, icon: IconStaff },
      { path: NAV_PATHS.officialPlayers, label: menu.players, icon: IconPlayer },
      { path: NAV_PATHS.officialPitches, label: menu.pitches, icon: IconGoalPost },
    ],
  },
  {
    roles: ANY_FACET,
    entries: [
      { path: NAV_PATHS.teams, label: menu.teams, icon: IconTeam, roles: TEAMS_READERS },
      {
        path: NAV_PATHS.matches,
        label: menu.matches,
        icon: IconCalendar,
        roles: CALENDAR_READERS,
      },
      { path: NAV_PATHS.leagues, label: menu.leagues, icon: IconTrophy },
    ],
  },
]

// The longest matching section wins, so `/me/official/people/71` marks «Staff» and not the
// dashboard — whose section is a prefix of every other one under `/me/official`.
function activeSection(sections: readonly string[], path: string): string | null {
  const matching = sections.filter((section) => path === section || path.startsWith(`${section}/`))
  return matching.sort((a, b) => b.length - a.length)[0] ?? null
}

function entriesFor(group: GroupSpec, roles: readonly Role[]): readonly EntrySpec[] {
  if (!group.roles.some((role) => roles.includes(role))) return []
  return group.entries.filter(
    (entry) => entry.roles === undefined || entry.roles.some((role) => roles.includes(role)),
  )
}

/** The menu for who is looking and where they are. One flat list, no headings: empty when
 *  there is nothing to offer. */
export function navigationEntries(
  roles: readonly Role[],
  currentPath: string,
): readonly NavigationEntry[] {
  const visible = GROUPS.flatMap((group) => entriesFor(group, roles))
  const current = activeSection(
    visible.map((entry) => entry.path),
    currentPath,
  )

  return visible.map(({ path, label, icon }) => ({
    path,
    label,
    icon,
    active: path === current,
  }))
}
